import { LinkAdd20Regular, LinkDismiss20Regular } from "@fluentui/react-icons";
import { useRichTextEditorContext } from "mui-tiptap";
import { useState, useEffect, useCallback } from "react";
import { Button, Tooltip, ButtonGroup } from "@mui/material";
import LinkDialog from "./LinkDialog";
import { Tool } from "../../models/Tool";
import { useTranslation } from "react-i18next";

type MenuButtonImageLinkProps = {
  tooltipLabel: string;
  ariaLabel: string;
  onLinkInsert?: (tool: Tool) => void;
  onLinkRemove?: (toolId: string) => void;
  selectedTools?: Tool[];
};

export default function MenuButtonImageLink({
  tooltipLabel,
  ariaLabel,
  onLinkInsert,
  onLinkRemove,
  selectedTools,
}: MenuButtonImageLinkProps) {
  const editor = useRichTextEditorContext();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isImageSelected, setIsImageSelected] = useState(false);
  const [currentLink, setCurrentLink] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!editor) return;

    const updateImageSelection = () => {
      const { state } = editor;
      const { selection } = state;
      const node = state.doc.nodeAt(selection.from);

      const isImage = node?.type.name === "image";
      setIsImageSelected(isImage);

      if (isImage) {
        // Check if image already has a link mark
        const linkMark = node.marks.find((mark) => mark.type.name === "link");
        setCurrentLink(linkMark?.attrs.href || null);
      } else {
        setCurrentLink(null);
      }
    };

    editor.on("selectionUpdate", updateImageSelection);
    updateImageSelection();

    return () => {
      editor.off("selectionUpdate", updateImageSelection);
    };
  }, [editor]);

  const handleClick = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleApply = useCallback(
    (url: string, fullTool?: Tool) => {
      if (!editor || !url.trim()) return;

      const { state } = editor;
      const { selection } = state;
      const node = state.doc.nodeAt(selection.from);

      if (node?.type.name === "image") {
        const from = selection.from;
        const to = from + node.nodeSize;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const linkAttrs: Record<string, any> = {
          href: url,
          target: "_blank",
          rel: "noopener noreferrer nofollow",
        };

        if (fullTool?.toolId) {
          linkAttrs["data-tool-link-id"] = fullTool.toolId;
        }

        editor
          .chain()
          .focus()
          .setTextSelection({ from, to })
          .unsetMark("link")
          .setMark("link", linkAttrs)
          .setNodeSelection(from) // Use setNodeSelection to keep the image highlighted
          .run();
      }

      if (fullTool && onLinkInsert) {
        onLinkInsert(fullTool);
      }

      setDialogOpen(false);
    },
    [editor, onLinkInsert]
  );

  const handleRemoveLink = useCallback(() => {
    if (!editor) return;

    const { state } = editor;
    const { selection } = state;
    const node = state.doc.nodeAt(selection.from);

    if (node?.type.name === "image") {
      const from = selection.from;
      const to = from + node.nodeSize;

      // Get the toolId before removing the link
      const linkMark = node.marks.find((mark) => mark.type.name === "link");
      const toolId = linkMark?.attrs["data-tool-link-id"];

      // Remove the link mark from the image
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .unsetMark("link")
        .setNodeSelection(from)
        .run();

      // Update state to hide the remove button
      setCurrentLink(null);

      // Notify parent if a tool link was removed
      if (toolId && onLinkRemove) {
        onLinkRemove(toolId);
      }
    }
  }, [editor, onLinkRemove]);

  return (
    <>
      <ButtonGroup variant="text" size="small">
        <Tooltip title={tooltipLabel}>
          <Button
            aria-label={ariaLabel}
            disabled={!editor?.isEditable || !isImageSelected}
            onClick={handleClick}
            sx={{
              minWidth: "auto",
              padding: "4px 8px",
              color: currentLink ? "primary.main" : "text.primary",
              "&:hover": {
                backgroundColor: "action.hover",
              },
            }}
          >
            <LinkAdd20Regular />
          </Button>
        </Tooltip>
        {currentLink && (
          <Tooltip title={t("removeImageLink")}>
            <Button
              aria-label={t("removeImageLink")}
              disabled={!editor?.isEditable}
              onClick={handleRemoveLink}
              sx={{
                minWidth: "auto",
                padding: "4px 8px",
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <LinkDismiss20Regular />
            </Button>
          </Tooltip>
        )}
      </ButtonGroup>
      <LinkDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onApply={handleApply}
        currentLink={currentLink}
        selectedTools={selectedTools}
      />
    </>
  );
}
